// Chrome y Edge graban las notas de voz en WebM sin poner la duración en el archivo: el
// reproductor no sabe cuánto dura, no muestra el total y la barra no deja adelantar. Aquí
// se le agrega en la cabecera (Segment > Info > Duration) antes de subirla, así sirve en
// cualquier reproductor, también descargada. Si el archivo no tiene la forma que deja el
// navegador, se envía tal cual.

const ID = {
  EBML: 0x1a45dfa3,
  SEGMENTO: 0x18538067,
  INFO: 0x1549a966,
  // TimecodeScale: nanosegundos por unidad de tiempo (1 ms si no viene).
  ESCALA: 0x2ad7b1,
  DURACION: 0x4489,
  // Índice con posiciones dentro del archivo: al insertar bytes quedarían corridas.
  INDICE: 0x114d9b74,
  CLUSTER: 0x1f43b675,
};
const BYTES_INFO = [0x15, 0x49, 0xa9, 0x66];
// Info va en los primeros cien bytes; con esto sobra.
const BYTES_CABECERA = 4096;

/** Número de largo variable de EBML. Los ids conservan la marca de largo; los tamaños no. */
const leerVint = (bytes, posicion, { conMarca = false } = {}) => {
  const primero = bytes[posicion];
  if (!primero) return null;
  let largo = 1;
  while (!(primero & (0x80 >> (largo - 1)))) largo += 1;
  if (posicion + largo > bytes.length) return null;
  let valor = conMarca ? primero : primero & (0xff >> largo);
  // Todo en unos es "tamaño desconocido": así deja Chrome el segmento y los clusters.
  let todoUnos = valor === 0xff >> largo;
  for (let i = 1; i < largo; i += 1) {
    valor = valor * 256 + bytes[posicion + i];
    if (bytes[posicion + i] !== 0xff) todoUnos = false;
  }
  return { valor, largo, desconocido: !conMarca && todoUnos };
};

/** { id, inicio, inicioDatos, fin } con fin null si el tamaño es desconocido. */
const leerElemento = (bytes, posicion) => {
  const id = leerVint(bytes, posicion, { conMarca: true });
  const tamano = id && leerVint(bytes, posicion + id.largo);
  if (!tamano) return null;
  const inicioDatos = posicion + id.largo + tamano.largo;
  return { id: id.valor, inicio: posicion, inicioDatos, fin: tamano.desconocido ? null : inicioDatos + tamano.valor };
};

const leerEntero = (bytes, desde, hasta) => {
  let valor = 0;
  for (let i = desde; i < hasta; i += 1) valor = valor * 256 + bytes[i];
  return valor;
};

const escribirVint = (valor) => {
  let largo = 1;
  while (valor >= 2 ** (7 * largo) - 1) largo += 1;
  const bytes = new Uint8Array(largo);
  let resto = valor;
  for (let i = largo - 1; i >= 0; i -= 1) {
    bytes[i] = resto % 256;
    resto = Math.floor(resto / 256);
  }
  bytes[0] |= 0x80 >> (largo - 1);
  return bytes;
};

/**
 * Info con la duración agregada.
 * @param {Uint8Array} cabecera - el comienzo del archivo
 * @param {number} duracionMs
 * @returns {{ antes: Uint8Array, info: Uint8Array, desde: number }|null} lo que va antes de
 *   Info, el Info nuevo y desde qué byte sigue el archivo original; null si no se toca.
 */
export const infoConDuracion = (cabecera, duracionMs) => {
  if (!(duracionMs > 0)) return null;
  const ebml = leerElemento(cabecera, 0);
  if (!ebml || ebml.id !== ID.EBML || ebml.fin === null) return null;
  const segmento = leerElemento(cabecera, ebml.fin);
  // Con tamaño conocido habría que corregirlo también; el navegador lo deja desconocido.
  if (!segmento || segmento.id !== ID.SEGMENTO || segmento.fin !== null) return null;

  let info = null;
  for (let posicion = segmento.inicioDatos; !info;) {
    const elemento = leerElemento(cabecera, posicion);
    if (!elemento || elemento.fin === null || elemento.fin > cabecera.length) return null;
    if (elemento.id === ID.INDICE || elemento.id === ID.CLUSTER) return null;
    if (elemento.id === ID.INFO) info = elemento;
    posicion = elemento.fin;
  }

  let escala = 1000000;
  for (let posicion = info.inicioDatos; posicion < info.fin;) {
    const hijo = leerElemento(cabecera, posicion);
    if (!hijo || hijo.fin === null || hijo.fin > info.fin) return null;
    if (hijo.id === ID.DURACION) return null;
    if (hijo.id === ID.ESCALA) escala = leerEntero(cabecera, hijo.inicioDatos, hijo.fin);
    posicion = hijo.fin;
  }
  if (!(escala > 0)) return null;

  // Duration: id 0x4489, 8 bytes, número de punto flotante en unidades de la escala.
  const duracion = new Uint8Array(11);
  duracion.set([0x44, 0x89, 0x88]);
  new DataView(duracion.buffer).setFloat64(3, (duracionMs * 1e6) / escala);

  const datos = cabecera.subarray(info.inicioDatos, info.fin);
  const tamano = escribirVint(datos.length + duracion.length);
  const nuevo = new Uint8Array(BYTES_INFO.length + tamano.length + datos.length + duracion.length);
  nuevo.set(BYTES_INFO, 0);
  nuevo.set(tamano, BYTES_INFO.length);
  nuevo.set(datos, BYTES_INFO.length + tamano.length);
  nuevo.set(duracion, BYTES_INFO.length + tamano.length + datos.length);
  return { antes: cabecera.subarray(0, info.inicio), info: nuevo, desde: info.fin };
};

/** La nota con su duración, o la misma si no se pudo. Nunca falla. */
export const conDuracionWebm = async (blob, duracionMs) => {
  try {
    const cabecera = new Uint8Array(await blob.slice(0, BYTES_CABECERA).arrayBuffer());
    const arreglo = infoConDuracion(cabecera, duracionMs);
    if (!arreglo) return blob;
    return new Blob([arreglo.antes, arreglo.info, blob.slice(arreglo.desde)], { type: blob.type });
  } catch (error) {
    return blob;
  }
};
